// Air-to-air gun lead solution, shared by the HUD lead pip (main.js) and its
// regression test (tools/check_gun_lead.mjs) so the two can never drift.
//
// The server fires plane rounds at  Vel = shooter.Vel + dir*muzzle  and then
// integrates them under gravity (internal/game/world.go: fireGun, stepBullets).
// To put a round on a moving target we therefore have to solve for two things:
//
//   1. Deflection — because the round carries the shooter's own velocity, the
//      lead is built from the CLOSING (relative) velocity, target motion minus
//      our own, not the target's world-frame motion alone. (Leading by the full
//      target velocity biases the aim by ~ own-speed x time-of-flight.)
//   2. Drop — the linear intercept ignores gravity, so over the time of flight
//      the round falls 0.5*g*t^2. We raise the aim point by that much so the
//      round arcs back down onto the lead point.
//
// The result is the world point to lay the pipper on; pointing the guns there
// makes the tracer stream converge on the target.

import { GRAVITY, qConj, qRotate } from "./fdm.js";

/**
 * World-space aim point (deflection + gravity drop) for a forward-firing gun.
 *
 * @param selfPos    shooter position {x,y,z}
 * @param selfVel    shooter velocity {x,y,z} (rounds inherit this)
 * @param tgtPos     target position {x,y,z}
 * @param tgtVel     target velocity {x,y,z}
 * @param muzzleMS   muzzle speed (m/s) of the gun's caliber
 * @param out        optional {x,y,z} to write into (avoids per-frame allocation)
 * @param gravity    gravity (m/s^2); defaults to the shared flight-model value
 * @returns the out object (or a fresh one) with the aim point
 */
export function gunLeadPoint(selfPos, selfVel, tgtPos, tgtVel, muzzleMS, out = {}, gravity = GRAVITY) {
  const relX = tgtPos.x - selfPos.x;
  const relY = tgtPos.y - selfPos.y;
  const relZ = tgtPos.z - selfPos.z;
  const dist = Math.hypot(relX, relY, relZ) || 1e-6;

  // Closing (relative) velocity — target motion minus our own.
  const rvx = tgtVel.x - selfVel.x;
  const rvy = tgtVel.y - selfVel.y;
  const rvz = tgtVel.z - selfVel.z;

  // Range rate (+ = opening); a closing pass shortens the time of flight.
  const rangeRate = (rvx * relX + rvy * relY + rvz * relZ) / dist;
  const closing = Math.max(60, muzzleMS - rangeRate);
  const tof = dist / closing;

  // Deflection: lead by the closing velocity. Drop: raise by the fall the round
  // accrues over the flight so it lands back on the lead point.
  out.x = tgtPos.x + rvx * tof;
  out.y = tgtPos.y + rvy * tof + 0.5 * gravity * tof * tof;
  out.z = tgtPos.z + rvz * tof;
  return out;
}

/**
 * World-space point a centered (no-deflection) burst actually reaches: the
 * wing-gun harmonization (convergence) point, dropped by the gravity fall the
 * rounds take getting there. Used to range the boresight gun reticle so the
 * glyph sits on the true point of impact instead of the bore line.
 *
 * @param selfPos      shooter position {x,y,z}
 * @param selfVel      shooter velocity {x,y,z} (rounds inherit this)
 * @param fwd          unit nose/bore direction {x,y,z}
 * @param convergenceM harmonization range (m) where the wing guns cross
 * @param muzzleMS     muzzle speed (m/s) of the gun's caliber
 * @param out          optional {x,y,z} to write into
 * @param gravity      gravity (m/s^2); defaults to the shared flight-model value
 * @returns the out object (or a fresh one) with the reticle point
 */
export function gunBoresightReticle(selfPos, selfVel, fwd, convergenceM, muzzleMS, out = {}, gravity = GRAVITY) {
  // Rounds reach the convergence range sooner for the speed we throw at them
  // (the airframe's forward velocity adds to the muzzle speed), so less drop.
  const fwdSpeed = Math.max(0, fwd.x * selfVel.x + fwd.y * selfVel.y + fwd.z * selfVel.z);
  const tof = convergenceM / (muzzleMS + fwdSpeed);
  const drop = 0.5 * gravity * tof * tof;
  out.x = selfPos.x + fwd.x * convergenceM;
  out.y = selfPos.y + fwd.y * convergenceM - drop; // gravity falls in world -Y
  out.z = selfPos.z + fwd.z * convergenceM;
  return out;
}

// Where a legacy battery's barrels sit (internal/game/config.go
// resolveBattery): 1.2 m ahead of the airframe's origin and 0.3 m under it,
// spread along the span. A real battery names its own muzzles (battery.js),
// and gunSightLine takes the sighting mount's.
export const GUN_MUZZLE_FWD_M = 1.2;
export const GUN_MUZZLE_DOWN_M = 0.3;
const LEGACY_MUZZLE = { x: GUN_MUZZLE_FWD_M, y: -GUN_MUZZLE_DOWN_M };

/**
 * The reflector sight's line: the direction from the pilot's eye to where the
 * battery's rounds cross the harmonization range, in the BODY frame (nose +X,
 * up +Y, starboard +Z). The cockpit (cockpit.js) paints its reticle down this
 * line, so the pipper sits on the rounds' point of impact.
 *
 * It is measured from the airframe that fired, which carries on at the very
 * velocity the rounds inherited: from there a round flies straight down its
 * barrel at the muzzle speed and falls, whatever the airframe's speed or
 * angle of attack. That's the picture from the seat (the tracers meet on the
 * pipper) and the shot on a bandit flying our way at our speed, the tail
 * chase every gunsight is harmonized for. A crossing or closing bandit is the
 * lead pip's job (gunLeadPoint), which works in the same frame — so the pip
 * and the pipper never disagree about how far a round falls.
 *
 * Two things push the line below the eye's own boresight: the rounds converge
 * on the NOSE line, which runs under the eye (0.68 m in a Spitfire, 0.92 m in
 * a Lightning), and they fall on the way. stepBullets is semi-implicit Euler
 * (gravity into the velocity, then the position), so after k ticks a round has
 * fallen g·dt²·k(k+1)/2: the continuous ½gt² plus ½g·t·dt, 5 cm at 280 m.
 * Gravity's pull is read in the body frame, so rolled over, the pipper sags
 * toward the ground, not toward the cockpit floor.
 *
 * @param eye          body-frame eye point {x,y,z} (the office's origin)
 * @param att          attitude quaternion {x,y,z,w} (gravity's direction only)
 * @param convergenceM harmonization range (m): where fireGun aims every barrel
 * @param muzzleMS     muzzle speed (m/s) of the gun's caliber
 * @param tickDt       the server's tick (s), for the integrator's extra fall
 * @param out          optional {x,y,z} to write into
 * @param gravity      gravity (m/s^2); defaults to the shared flight-model value
 * @param muzzle       the sighting mount's mean muzzle {x, y}, body frame
 *                     (battery.js sightingMuzzle); defaults to the legacy
 *                     block's 1.2 m ahead, 0.3 m under
 * @returns the out object (or a fresh one): a unit vector, body frame
 */
export function gunSightLine(eye, att, convergenceM, muzzleMS, tickDt = 1 / 60, out = {}, gravity = GRAVITY, muzzle = LEGACY_MUZZLE) {
  // World "down" in the body frame.
  const g = qRotate(qConj(att), { x: 0, y: -1, z: 0 });
  // Flight time from the (centreline) muzzle to the convergence point: the
  // run down the barrel at the muzzle speed, then one Newton step for the
  // fall along the nose (a dive hurries the round to the range, a climb holds
  // it back). A wing barrel's spanwise offset lengthens the run by well under
  // a millimetre of fall, and its left and right rounds straddle the line.
  const m = Math.max(1, muzzleMS);
  const mx = muzzle?.x ?? GUN_MUZZLE_FWD_M;
  const down = -(muzzle?.y ?? -GUN_MUZZLE_DOWN_M); // how far under the nose line the muzzle sits
  const run = Math.hypot(convergenceM - mx, down);
  const fall = (t) => 0.5 * gravity * t * (t + tickDt);
  const t0 = run / m;
  const t = t0 - (g.x * fall(t0)) / ((m * (convergenceM - mx)) / run + g.x * gravity * (t0 + 0.5 * tickDt));
  const drop = fall(t);
  // Where it crosses the range: on the nose line, but for the fall and the
  // sliver of barrel climb a changed flight time leaves in it.
  const x = convergenceM - eye.x;
  const y = down * (t / t0 - 1) + g.y * drop - eye.y;
  const z = g.z * drop - eye.z;
  const n = Math.hypot(x, y, z) || 1;
  out.x = x / n;
  out.y = y / n;
  out.z = z / n;
  return out;
}
